import React, { useState, useEffect, useMemo } from "react";
import UpdateToolForm from "./update-tool.form";
import { LinkedProduct, ProductLicense, UpdateToolFormValues } from "./update-tool.types";

import { Spinner } from "react-bootstrap";
import OrderSummary from "../../../../Common/loader";
import './update-tool-modal.styles.scss';
import Suspense from "../../../../Common/suspense.component";
import { TechnologyListItem } from "../../../../../common/types/tool.types";
import useActions from "../../../../../hooks/service-actions.hook";
import { ToolInfoEffectActions } from "../../../../../common/services/security-stack-view/tool-info.service";
import { useEntity } from "../../../../../hooks/use-entity.hook";
import { GetToolInfoResponse, PracticeToolSelected } from "../../../../../common/services/security-stack-view/dto/get-tool-info.dto";
import { Entities } from "../../../../../common/store/entities/entities.enum";
import useLoaders from "../../../../../hooks/use-loaders.hook";
import { Loaders } from "../../../../../common/store/loaders/loaders.enum";
import MyModal from "../../../../Common/my-modal/my-modal.component";
import ImageLoader from "../../../../Common/ImageLoader";
import integrationUtils from "../../../../../Utils/integration.utils";
import TrashCanFillIcon from "../../../../icons/trash-can-fill.icon,";

interface UpdateToolModalProps {
  show: boolean;
  tool: any;
  toolInfo?: {
    reason: string;
    monitoringOption: string;
    linkedProducts: LinkedProduct[];
    productLicenses: ProductLicense[];
    technologiesList: TechnologyListItem[];
    selectedToolsIds: number[];
    selectedTools?: any[];
    isPractice: boolean;
  },
  practice?: any;
  sourcePractice?: any;
  isRemovingTool: boolean;
  clientId?: number;
  showToolInfo?: boolean;
  technologiesTitle?: string;
  showCreateLicenses?: boolean;
  showRemoveButton?: boolean;
  isView?: boolean;
  onHide: () => void;
  onSave: (toolData: any) => void;
  onRemove: ({ isCustom }: { isCustom?: boolean }) => void;
  onSaveAndAddAnother: (practice: any) => void;
  refreshData: () => void;
}

const mergeLists = <T extends { value: any; selected?: boolean }>(
  preselectedList: T[],
  existingList: T[],
  shouldPrefer: (item: T) => boolean = (item) => item.selected === true
): T[] => {
  return [...preselectedList, ...existingList].reduce((acc: T[], item: T) => {
    const existingIndex = acc.findIndex((existing: T) => existing.value === item.value);
    if (existingIndex === -1) {
      acc.push(item);
    } else if (shouldPrefer(item) && !shouldPrefer(acc[existingIndex])) {
      acc[existingIndex] = item;
    }
    return acc;
  }, []);
};

const UpdateToolModal: React.FC<UpdateToolModalProps> = ({
  show,
  clientId,
  tool,
  toolInfo,
  practice,
  sourcePractice,
  isRemovingTool = false,
  showToolInfo = true,
  technologiesTitle = "Do you use this tool for another Technology?",
  showCreateLicenses = false,
  showRemoveButton = true,
  isView = false,
  onHide,
  onSave = () => {},
  onRemove = () => {},
  onSaveAndAddAnother = (practice: any) => {},
  refreshData = () => {},
}) => {
  const activePractice = practice;
  const [toolData, setToolData] = useState<any>(null);
  const { getToolInfo, getPracticeTypes } = useActions(ToolInfoEffectActions);

  const toolId = tool?.obj_IntegrationId || 0;

  const entityId = clientId ? `${clientId}-${toolId}` : toolId;

  const { entityData: toolInfoData } = useEntity<GetToolInfoResponse>({ entityType: Entities.ToolInfo, entityId: entityId });
  const { isLoading: isUpdatingTool } = useLoaders({ loaders: Loaders.UpdateToolLoader });
  const { isLoading: isToolInfoLoading } = useLoaders({ loaders: Loaders.GetToolInfoLoader });

  useEffect(() => {
    if (!show) return;
    getToolInfo({ toolId, clientId });
    getPracticeTypes({ toolId, clientId });
  }, [show, toolId, clientId]);

  const isNewTool = !tool?.obj_IntegrationId;
  const showRemoveButtonLocal = showRemoveButton && !isNewTool && !activePractice;
  const isCustomTool = toolInfoData?.IntegrationInfo_List?.[0]?.IsCustom;

  const initialValues = useMemo(() => {
    const toolInfoFromList = toolInfoData?.IntegrationInfo_List?.[0];
    const licensesList = (toolInfo?.productLicenses || toolInfoFromList?.ToolProductLicense_List || [])?.map((item: any) => ({
      id: item.ProductId,
      name: item.ProductName,
      quantity: item.Quantity,
      pricePerUnit: item.Cost
    }));
    const linkedProductsList = (toolInfo?.linkedProducts || toolInfoFromList?.ProductList_Selected || [])?.map((item: any) => ({
      ...item,
      id: item.ObjectId,
      name: item.Name,
    }));

    const buildTechnologiesList = (source: any[], filterFn: (item: any) => boolean, getSelectedValue: (item: any) => boolean) => {
      return source
        ?.filter(filterFn)
        ?.map((item: any) => ({
          ...item,
          value: item.IntegrationTechnologyTypeEnum,
          label: item.IntegrationTechnologyTypeName,
          selected: getSelectedValue(item)
        })) || [];
    };

    const technologiesList = buildTechnologiesList(
      toolInfoData?.IntegrationTechnologyType_List || [],
      (item: any) => item.ExistsInTechnologyStack,
      (item: any) => item.ExistsInTechnologyStack
    );
    const preselecteTechnologyList = buildTechnologiesList(
      toolInfoData?.IntegrationTechnologyType_List || [],
      (item: any) => tool?.technologyList?.includes(item.IntegrationTechnologyTypeEnum),
      () => true
    );
    const technologyListMerged = mergeLists(preselecteTechnologyList, technologiesList);

    const buildPracticeTypesList = (source: any[], filterFn: (item: any) => boolean, getSelectedValue: (detail: any) => boolean) => {
      return source
        ?.filter(filterFn)
        ?.map((item: any) => ({
          ...item,
          value: item.TechnologyTypeEnum,
          label: item.TechnologyName,
          practiceTypes: item.PracticeDetail_List?.map((detail: any) => ({
            ...detail,
            value: detail.PracticeId,
            label: detail.PracticeName,
            selected: getSelectedValue(detail)
          })) || []
        })) || [];
    };

    const practiceTypesList = buildPracticeTypesList(
      toolInfoData?.practiceTypes || [],
      (item: any) => item.PracticeDetail_List?.some((detail: any) => detail.IsExistInSecurityStack),
      (detail: any) => detail.IsExistInSecurityStack
    );
    const preselectedPracticeTypes = activePractice?.preselectedPracticeTypes?.map((item: any) => Number(item));
    const preselectedPracticeTypesList = buildPracticeTypesList(
      toolInfoData?.practiceTypes || [],
      (item: any) => item.PracticeDetail_List?.some((detail: any) => preselectedPracticeTypes?.includes(Number(detail.PracticeId))),
      (detail: any) => preselectedPracticeTypes?.includes(Number(detail.PracticeId))
    );
    const practiceTypesListMerged = mergeLists(
      preselectedPracticeTypesList,
      practiceTypesList,
      (item: any) => item.practiceTypes?.some((practiceType: any) => practiceType.selected)
    );
    return {
      toolName: tool?.ToolName,
      website: tool?.Website || toolInfoFromList?.URL || "",
      isConnected: tool?.IsConnected || toolInfoFromList?.IsConnected || false,
      reason: toolInfo?.reason || toolInfoData?.IntegrationInfo_List?.[0]?.Reason || "",
      monitoringOption: toolInfo?.monitoringOption || toolInfoFromList?.MonitoredBySOC_Selected?.DDLText || toolInfoData?.MonitoredBySOC_List?.[0]?.DDLText || "",
      linkedProducts: linkedProductsList,
      productLicenses: licensesList,
      technologiesList:  technologyListMerged,
      practiceTypesList: practiceTypesListMerged,
      licenseCreation: {
        knowPrice: "",
        currency: "GBP",
        price: 10.00,
        priceUnit: "/user",
        priceInterval: "/month",
        licensesQty: 10
      }
    }
  }, [tool, toolInfo, toolInfoData, activePractice]);

  const monitoringOptions = useMemo(() => {
    return toolInfoData?.MonitoredBySOC_List?.map((item: any) => ({
      value: item.DDLText,
      label: item.DDLText
    })) || [];
  }, [toolInfoData]);

  const technologiesList = useMemo(() => {
    if (!toolInfoData?.IntegrationTechnologyType_List?.length) return [];
    return toolInfoData.IntegrationTechnologyType_List
      .filter((item) => !item.ExistsInTechnologyStack)
      .map((item) => ({
        ...item,
        value: String(item.IntegrationTechnologyTypeEnum),
        label: item.IntegrationTechnologyTypeName,
        selected: item.ExistsInTechnologyStack
      }));
  }, [toolInfoData]);

  const availablePracticeTypesList = useMemo(() => {
    if (!toolInfoData?.practiceTypes?.length) return [];
    return toolInfoData.practiceTypes.map((item: any) => ({
      ...item,
      value: item.TechnologyTypeEnum,
      label: item.TechnologyName,
      practiceTypes: item.PracticeDetail_List?.map((detail: any) => ({
        ...detail,
        value: detail.PracticeId,
        label: detail.PracticeName,
        selected: detail.IsExistInSecurityStack
      }))
    }));
  }, [toolInfoData]);

  const onSubmit = (values: UpdateToolFormValues) => {
    setToolData(values);
  }

  const hasSelectedTechnology = useMemo(() => {
    const technologiesList = toolData?.technologiesList
    if (!technologiesList) return false;
    return technologiesList.some((item: any) => item.selected);
  }, [toolData]);

  const hasSelectedPractice = useMemo(() => {
    const practiceTypesList = toolData?.practiceTypesList;
    if (!practiceTypesList) return false;
    return practiceTypesList.some((practice: any) => 
      practice.practiceTypes?.some((practiceType: any) => practiceType.selected)
    );
  }, [toolData]);

  const handleOnSave = ({ addAnother = false }: { addAnother?: boolean } = {}) => {
    const integrationInfo = {
      ...(toolInfoData?.IntegrationInfo_List?.[0] ?? {}),
      MonitoredBySOC_Selected: toolInfoData?.MonitoredBySOC_List.find((item: any) => item.DDLText === toolData.monitoringOption),
      URL: toolData.website,
      Reason: toolData.reason,
      ProductList_Selected: toolData.linkedProducts.map((item: any) => ({
        ...item,
        ObjectId: item.id,
        Name: item.name,
      })),
    }

    const monitoredBySOCSelected = toolInfoData?.MonitoredBySOC_List.find((item: any) => item.DDLText === toolData.monitoringOption);
    const isManagedBySoc = monitoredBySOCSelected?.DDLText?.toLowerCase().includes('yes') || 
                          monitoredBySOCSelected?.DDLText?.toLowerCase().includes('managed') ||
                          (toolData.monitoringOption?.toLowerCase().includes('yes') || toolData.monitoringOption?.toLowerCase().includes('managed'));

    const practiceToolSelectedList: PracticeToolSelected[] = (toolInfo?.selectedTools || []).map((selectedTool: any) => ({
      ToolId: selectedTool.IntegrationId || selectedTool.ObjectId || 0,
      ToolName: selectedTool.IntegrationName || selectedTool.Name || '',
      ToolIcon: selectedTool.ToolIcon || selectedTool.Icon || '',
      IsManagedBySoc: isManagedBySoc || false,
      Enum_IntegrationTechnology: activePractice?.TechnologyEnum || 0,
    }));
    const practiceTools = availablePracticeTypesList?.length > 0 ? [toolId] : [];
    const isPractice = availablePracticeTypesList?.length > 0;
    onSave({
      ToolName: activePractice?.ToolName || toolData.toolName,
      ToolId: activePractice?.IntegrationId || toolId,
      IsPractice: toolInfo?.isPractice || isPractice || false,
      PracticeToolIdsList: toolInfo?.selectedToolsIds || practiceTools,
      PracticeToolSelected_List: practiceToolSelectedList,
      technologyIdsList: toolData.technologiesList
        ?.filter((item: any) => item.selected)
        .map((item: any) => Number(item.value)),
      technologiesList: toolData.technologiesList?.map((item: any) => ({ 
        ...item,
        ExistsInTechnologyStack: item.selected
      })),
      IntegrationInfo_List: [integrationInfo],
      PracticeType_List: toolData.practiceTypesList?.map((item: any) => ({
        ...item,   
        PracticeDetail_List: item.practiceTypes.map((practiceType: any) => ({
          ...practiceType,
          IsExistInSecurityStack: practiceType.selected,
        })),
      })),
      callback: () => {
        refreshData();
        if ((activePractice || sourcePractice) && addAnother) {
          onSaveAndAddAnother(activePractice || sourcePractice);
        }
      }
    });
  }

  const handleOnRemove = () => {
    const toolInfoFromList = toolInfoData?.IntegrationInfo_List?.[0];
    const isCustom = toolInfoFromList?.IsCustom;
    onRemove({ isCustom });
  }

  const onValidChange = (_isValid: boolean) => {}

  return (
    <MyModal show={show} onHide={onHide} className="update-tool-modal">
      <MyModal.Header closeButton={true} onCrossClick={onHide}>
        <div className="d-flex justify-content-center w-100 align-items-center gap-8">
          <div className="d-flex justify-content-center align-items-center gap-4">
            <ImageLoader
              alt=""
              width={16}
              height={16}
              className="rounded-full"
              style={{ marginLeft: '3px' }}
              src={integrationUtils.getImagePath(tool?.obj_IntegrationId, tool?.ToolIcon)}
              noImageComponent={(
                <img
                  alt=""
                  width={16}
                  height={16}
                  className="rounded-full"
                  style={{ marginLeft: '3px' }}
                  src={"/images/attack-surface/generic2-dark.svg"}
                />
              )}
            />
            <span className="f-14 line-height-22 f-black f-500">
              {tool?.ToolName}
            </span>
          </div>
          {isToolInfoLoading && (
            <Spinner
              animation="border"
              size="sm"
              variant="secondary"
              style={{
                width: "12px",
                height: "12px",
              }}
            />
          )}
        </div>
      </MyModal.Header>
      <MyModal.Body className="p-0 overflow-x-hidden hide-scrollbar">
        <Suspense 
          loaders={Loaders.GetToolInfoLoader} 
          fallback={
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
              <OrderSummary />
            </div>
          }
          forceShow={!!toolInfoData?.IntegrationInfo_List}
        >
          <UpdateToolForm 
            initialValues={initialValues} 
            monitoringOptions={monitoringOptions} 
            technologiesList={technologiesList}
            practiceTypesList={availablePracticeTypesList}
            isNewTool={isNewTool} 
            showPracticeTypes={availablePracticeTypesList?.length > 0}
            showToolInfo={showToolInfo}
            showCreateLicenses={showCreateLicenses}
            technologiesTitle={technologiesTitle}
            isView={isView}
            onSubmit={onSubmit} 
            onValidChange={onValidChange} 
          />
        </Suspense>
      </MyModal.Body>
      <MyModal.Footer className="d-flex align-items-center justify-content-between gap-8">
        <div>
          {showRemoveButtonLocal && !isView && (
            <button className="my-button d-flex align-items-center gap-4 pointer my-button styled bg-transparent" onClick={handleOnRemove}>
              <div className="left-icon d-flex align-items-center justify-content-center" style={{ width: '24px', height: '24px' }}>
                {isRemovingTool ? (
                  <Spinner
                    animation="border"
                    size="sm"
                    variant="secondary"
                    style={{
                      width: "16px",
                      height: "16px",
                    }}
                  />
                ) : <TrashCanFillIcon className="f-grey" />}
              </div>
              <span className="f-14 line-height-22 f-500 f-grey">
                {isCustomTool ? 'Delete' : 'Remove'}
              </span>
            </button>
          )}
        </div>
        {!isView && (
          <div className="d-flex gap-8">
            <MyModal.CancelButton onClick={onHide}>
              Cancel
            </MyModal.CancelButton>
            <MyModal.ApplyButton
              onClick={() => {
                handleOnSave();
              }}
              disabled={isUpdatingTool || isToolInfoLoading || (!hasSelectedTechnology && !hasSelectedPractice)}
            >
              {isUpdatingTool ? (
                <div className="d-flex align-items-center gap-8">
                  <Spinner
                    animation="border"
                    size="sm"
                    variant="secondary"
                    style={{
                      width: "16px",
                      height: "16px",
                    }}
                  />
                  <span>Saving...</span>
                </div>
              ) : (
                "Save Tool"
              )}
            </MyModal.ApplyButton>
            {activePractice && (
              <MyModal.ApplyButton
                onClick={() => {
                  handleOnSave({ addAnother: true });
                }}
                disabled={isUpdatingTool || isToolInfoLoading || (!hasSelectedTechnology && !hasSelectedPractice)}
              >
                {isUpdatingTool ? (
                  <div className="d-flex align-items-center gap-8">
                    <Spinner
                      animation="border"
                      size="sm"
                      variant="secondary"
                      style={{
                        width: "16px",
                        height: "16px",
                      }}
                    />
                    <span>Saving...</span>
                  </div>
                ) : "Save and Add Another"}
              </MyModal.ApplyButton>
            )}
          </div>
        )}
      </MyModal.Footer>
    </MyModal>
  );
};

export default UpdateToolModal;
